/**
 * Geocoding and driving distance via Geoapify.
 *
 * Always the `api-eu` host: Geoapify's DPA guarantees processing stays inside
 * the EU only for that endpoint, while the default `api.geoapify.com` routes
 * through Cloudflare. Passenger addresses are personal data, so the extra
 * hostname is the whole point.
 *
 * Why Geoapify and not the obvious alternatives: OpenRouteService forbids
 * client-side key use outright, Nominatim's policy names client-side
 * autocomplete as unacceptable use, Google is an independent controller whose
 * terms tell you not to send European end users' personal data and forbid
 * storing derived distances, and Mapbox's free geocoding tier forbids storing
 * results at all. A ten-year tax record needs a provider that permits
 * retention. See `01_Input/rechtliche-anforderungen.md`.
 *
 * The key is restricted by HTTP referrer in the Geoapify console. That is
 * abuse control, not secrecy — a key shipped to a browser is public, and no
 * amount of obfuscation changes it.
 */

const BASE_URL = 'https://api-eu.geoapify.com';

/** Shown next to the key field in the settings, so the caveats travel with it. */
export const GEOAPIFY_KEY_HELP =
  'Kostenlos unter geoapify.com. Den Schlüssel in der Geoapify-Konsole auf die ' +
  'Domain dieser App beschränken (HTTP-Referrer) und ein Ausgabenlimit setzen — ' +
  'ein Schlüssel im Browser ist grundsätzlich öffentlich. Ohne Schlüssel ' +
  'funktioniert alles außer der Adresssuche und der automatischen Entfernung.';

export interface GeoapifyConfig {
  apiKey: string;
  /** Bias results towards the operating area. */
  biasLat: number;
  biasLon: number;
  countryCode: string;
}

export interface GeocodeResult {
  formatted: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country: string;
  lat: number;
  lon: number;
  /** Geoapify's own confidence, 0..1. */
  confidence: number;
  /** `place_id`, useful for deduplicating repeated lookups. */
  placeId?: string;
}

export class GeoapifyError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'GeoapifyError';
  }
}

export class GeoapifyNotConfiguredError extends Error {
  constructor() {
    super('Kein Geoapify-API-Key hinterlegt. Entfernung bitte manuell eintragen.');
    this.name = 'GeoapifyNotConfiguredError';
  }
}

interface AutocompleteFeature {
  properties: {
    formatted?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
    city?: string;
    country_code?: string;
    lat?: number;
    lon?: number;
    rank?: { confidence?: number };
    place_id?: string;
  };
}

async function request<T>(url: string, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new GeoapifyError(
      'Keine Verbindung zum Geocoding-Dienst. Entfernung bitte manuell eintragen.',
    );
  }

  if (response.status === 401 || response.status === 403) {
    throw new GeoapifyError(
      'Geoapify hat den Zugriff abgelehnt. Prüfen Sie den API-Key und die erlaubten Referrer.',
      response.status,
    );
  }
  if (response.status === 429) {
    throw new GeoapifyError('Tageskontingent von Geoapify erschöpft.', 429);
  }
  if (!response.ok) {
    throw new GeoapifyError(`Geoapify antwortete mit Status ${response.status}.`, response.status);
  }

  return (await response.json()) as T;
}

/**
 * Address autocomplete.
 *
 * Callers must debounce. Geoapify bills per request and a keystroke-per-request
 * loop would burn the daily allowance on a single afternoon.
 */
export async function autocompleteAddress(
  config: GeoapifyConfig,
  query: string,
  signal?: AbortSignal,
): Promise<GeocodeResult[]> {
  if (!config.apiKey) throw new GeoapifyNotConfiguredError();
  if (query.trim().length < 3) return [];

  const url = new URL('/v1/geocode/autocomplete', BASE_URL);
  url.searchParams.set('text', query);
  url.searchParams.set('apiKey', config.apiKey);
  url.searchParams.set('lang', 'de');
  url.searchParams.set('limit', '6');
  url.searchParams.set('filter', `countrycode:${config.countryCode}`);
  url.searchParams.set('bias', `proximity:${config.biasLon},${config.biasLat}`);

  const data = await request<{ features?: AutocompleteFeature[] }>(url.toString(), signal);

  return (data.features ?? [])
    .map((feature): GeocodeResult | null => {
      const p = feature.properties;
      if (typeof p.lat !== 'number' || typeof p.lon !== 'number') return null;
      return {
        formatted: p.formatted ?? '',
        street: p.street,
        houseNumber: p.housenumber,
        postalCode: p.postcode,
        city: p.city,
        country: (p.country_code ?? config.countryCode).toUpperCase(),
        lat: p.lat,
        lon: p.lon,
        confidence: p.rank?.confidence ?? 0,
        placeId: p.place_id,
      };
    })
    .filter((result): result is GeocodeResult => result !== null);
}

/** One-shot forward geocode, used when an address was typed but never picked. */
export async function geocodeAddress(
  config: GeoapifyConfig,
  query: string,
  signal?: AbortSignal,
): Promise<GeocodeResult | null> {
  if (!config.apiKey) throw new GeoapifyNotConfiguredError();

  const url = new URL('/v1/geocode/search', BASE_URL);
  url.searchParams.set('text', query);
  url.searchParams.set('apiKey', config.apiKey);
  url.searchParams.set('lang', 'de');
  url.searchParams.set('limit', '1');
  url.searchParams.set('filter', `countrycode:${config.countryCode}`);
  url.searchParams.set('bias', `proximity:${config.biasLon},${config.biasLat}`);

  const data = await request<{ features?: AutocompleteFeature[] }>(url.toString(), signal);
  const feature = data.features?.[0];
  if (!feature || typeof feature.properties.lat !== 'number' || typeof feature.properties.lon !== 'number') {
    return null;
  }

  const p = feature.properties;
  return {
    formatted: p.formatted ?? query,
    street: p.street,
    houseNumber: p.housenumber,
    postalCode: p.postcode,
    city: p.city,
    country: (p.country_code ?? config.countryCode).toUpperCase(),
    lat: p.lat!,
    lon: p.lon!,
    confidence: p.rank?.confidence ?? 0,
    placeId: p.place_id,
  };
}

export interface RouteResult {
  distanceMeters: number;
  durationSeconds: number;
}

/**
 * Driving distance between two coordinates.
 *
 * `mode=drive` follows the road network. Straight-line distance would be
 * wrong by 20-40 % in an urban network and is not a defensible figure to put
 * in a tax record.
 */
export async function routeDistance(
  config: GeoapifyConfig,
  from: { lat: number; lon: number },
  to: { lat: number; lon: number },
  signal?: AbortSignal,
): Promise<RouteResult> {
  if (!config.apiKey) throw new GeoapifyNotConfiguredError();

  const url = new URL('/v1/routing', BASE_URL);
  url.searchParams.set('waypoints', `${from.lat},${from.lon}|${to.lat},${to.lon}`);
  url.searchParams.set('mode', 'drive');
  url.searchParams.set('units', 'metric');
  url.searchParams.set('apiKey', config.apiKey);

  const data = await request<{
    features?: Array<{ properties?: { distance?: number; time?: number } }>;
  }>(url.toString(), signal);

  const properties = data.features?.[0]?.properties;
  if (!properties || typeof properties.distance !== 'number') {
    throw new GeoapifyError('Geoapify hat keine Route zwischen den Adressen gefunden.');
  }

  return {
    distanceMeters: Math.round(properties.distance),
    durationSeconds: Math.round(properties.time ?? 0),
  };
}

/**
 * Great-circle distance, in metres.
 *
 * Only used as a sanity check on a routed result — a road distance shorter
 * than the straight line means something went wrong. Never used as the
 * recorded distance.
 */
export function haversineMeters(
  from: { lat: number; lon: number },
  to: { lat: number; lon: number },
): number {
  const R = 6_371_000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(to.lat - from.lat);
  const dLon = toRad(to.lon - from.lon);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(a)));
}

export function attributionText(): string {
  return 'Geocoding und Routing: Powered by Geoapify';
}

export const ATTRIBUTION_URL = 'https://www.geoapify.com/';
